/**
 * Lifecycle banner — surfaces trial / grace / closed state at the top
 * of the app shell. Polls `GET /api/v1/me/billing-state`.
 *
 * Per design doc §11 (`docs/plans/2026-05-12-trial-lifecycle-design.md`)
 * and sub-issue #228:
 * - Render nothing when `enforcement_enabled === false` OR
 *   `lifecycle_state === 'paid'` (no banner needed).
 * - First-paint / error: render nothing (don't flash, don't block).
 * - CTA "Upgrade" visible to all roles; enabled only for owner/admin.
 *
 * Copy strings come straight from §11 — do not paraphrase.
 */
import { Link } from "react-router-dom";
import { AlertTriangle, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { useBillingState } from "@/hooks/use-api";
import { useAuth } from "@/hooks/use-auth";
import type { BillingState } from "@/types/api";

/** Roles that can trigger the upgrade flow (`billing.subscribe`). */
const UPGRADE_ROLES = ["owner", "admin"] as const;

/**
 * Settings landing page hosts the Billing tab. No tab query-param is
 * wired today, so we route to /app/settings and the user picks the
 * Billing tab. If we add `?tab=billing` later we can update here.
 */
const UPGRADE_TARGET = "/settings";

/** Per-state CTA spec. `closed` routes users to the support mailto rather
 * than the upgrade page — per design §11 the closed-state copy is "Contact
 * support to reactivate", and self-service upgrade does not reactivate. */
interface CtaSpec {
  label: string;
  /** Either an in-app route or an external href (e.g. mailto:). */
  href: string;
  /** When true, render an external <a> instead of a react-router <Link>. */
  external?: boolean;
  /** Tooltip shown when CTA is disabled for the current role. */
  disabledTitle: string;
}

interface BannerVariant {
  /** Tailwind utility classes for the wrapper background + text. */
  className: string;
  /** Icon component to render at the start of the banner. */
  Icon: typeof AlertTriangle;
  /** Test-id attribute — used for snapshot grouping + role-gate tests. */
  testId: string;
  message: string;
  cta: CtaSpec;
}

const UPGRADE_CTA: CtaSpec = {
  label: "Upgrade",
  href: UPGRADE_TARGET,
  disabledTitle: "Only owners and admins can upgrade the plan",
};

const SUPPORT_CTA: CtaSpec = {
  label: "Contact support",
  href: "mailto:support@prayingmantis.cloud",
  external: true,
  disabledTitle: "Only owners and admins can contact support to reactivate",
};

function formatDate(iso: string | null): string {
  if (!iso) return "";
  // Match the design-doc example "upgrade now" UX: a short, locale-aware
  // date. Avoid full datetime — the banner is space-constrained.
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

/**
 * Derive the per-state banner variant (copy, color, severity). Returns
 * null if no banner should render for this state.
 *
 * Trial sub-tiers from design §11:
 *   - days_remaining > 7  → subtle "Trial: {N} days remaining"
 *   - days_remaining ≤ 3  → prominent "Trial expires {date} — upgrade now"
 *   - 4–7 days falls into the subtle tier (the §11 split is >7 / ≤3, so
 *     4–7 reads as the same "informational" band as >7, just with a
 *     shorter window — the design doesn't define a third visual tier).
 */
function pickVariant(state: BillingState): BannerVariant | null {
  switch (state.lifecycle_state) {
    case "trial": {
      const days = state.days_remaining;
      // `days === 0` reads as "expires today" — the urgent date copy would
      // still show a same-day date, but the explicit "today" string is
      // clearer at the moment the trial flips. Backend `_days_until`
      // clamps negatives to 0 so we never see a past-date.
      if (days === 0) {
        return {
          className:
            "bg-amber-100 text-amber-950 dark:bg-amber-900/40 dark:text-amber-100 border-b border-amber-300/60 dark:border-amber-700/60",
          Icon: AlertTriangle,
          testId: "lifecycle-banner-trial-urgent",
          message: "Trial expires today — upgrade now",
          cta: UPGRADE_CTA,
        };
      }
      if (days !== null && days <= 3) {
        return {
          className:
            "bg-amber-100 text-amber-950 dark:bg-amber-900/40 dark:text-amber-100 border-b border-amber-300/60 dark:border-amber-700/60",
          Icon: AlertTriangle,
          testId: "lifecycle-banner-trial-urgent",
          message: `Trial expires ${formatDate(state.trial_expires_at)} — upgrade now`,
          cta: UPGRADE_CTA,
        };
      }
      // days > 7 OR 4–7 days OR null: subtle informational ribbon.
      // `days === null` is defensive — backend guarantees a non-null
      // `days_remaining` while `lifecycle_state === 'trial'`, but if that
      // invariant ever drifts we'd rather show a coherent fallback than
      // "Trial: days remaining".
      return {
        className:
          "bg-muted/60 text-foreground border-b border-border",
        Icon: Info,
        testId: "lifecycle-banner-trial",
        message:
          days !== null ? `Trial: ${days} days remaining` : "Trial active",
        cta: UPGRADE_CTA,
      };
    }
    case "trial_expired_grace": {
      const graceDays = state.grace_days_remaining ?? 0;
      return {
        className:
          "bg-orange-100 text-orange-950 dark:bg-orange-900/40 dark:text-orange-100 border-b border-orange-300/60 dark:border-orange-700/60",
        Icon: AlertTriangle,
        testId: "lifecycle-banner-grace",
        message: `Trial expired. Agents paused. ${graceDays} days to upgrade before account closes.`,
        cta: UPGRADE_CTA,
      };
    }
    case "closed":
      // Design §11: "Account closed. Contact support to reactivate." —
      // self-service upgrade does not reactivate a closed account, so
      // surface the support mailto instead of the billing settings link.
      return {
        className:
          "bg-destructive text-destructive-foreground border-b border-destructive/80",
        Icon: AlertTriangle,
        testId: "lifecycle-banner-closed",
        message: "Account closed. Contact support to reactivate.",
        cta: SUPPORT_CTA,
      };
    case "paid":
    default:
      return null;
  }
}

export function LifecycleBanner({ basePath = "/app" }: { basePath?: string }) {
  const { user } = useAuth();
  const { data, isError, isPending } = useBillingState();

  // First paint and error: render nothing.
  // - `isPending`: avoid a flash before the response lands.
  // - `isError`: the banner is informational, never block the app
  //   when the endpoint is unreachable. The error is already logged by
  //   the shared api fetcher.
  if (isPending || isError || !data) return null;

  // Flag-off short-circuit (design §5.2) — paid: nothing to show.
  if (!data.enforcement_enabled) return null;
  if (data.lifecycle_state === "paid") return null;

  const variant = pickVariant(data);
  if (!variant) return null;

  const canUpgrade =
    user?.roles?.some((r) => UPGRADE_ROLES.includes(r as (typeof UPGRADE_ROLES)[number])) ?? false;

  const ctaClasses = cn(
    buttonVariants({ size: "sm", variant: "default" }),
    !canUpgrade && "cursor-not-allowed opacity-50",
  );

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid={variant.testId}
      className={cn(
        "flex items-center justify-between gap-3 px-4 py-2 text-sm",
        variant.className,
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        <variant.Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="truncate font-medium">{variant.message}</span>
      </div>
      {canUpgrade ? (
        variant.cta.external ? (
          <a href={variant.cta.href} className={ctaClasses}>
            {variant.cta.label}
          </a>
        ) : (
          <Link to={`${basePath}${variant.cta.href}`} className={ctaClasses}>
            {variant.cta.label}
          </Link>
        )
      ) : (
        <button
          type="button"
          disabled
          aria-disabled="true"
          title={variant.cta.disabledTitle}
          className={ctaClasses}
        >
          {variant.cta.label}
        </button>
      )}
    </div>
  );
}
